import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { FiUsers, FiCalendar, FiBook, FiDollarSign, FiUserCheck, FiTrendingUp } from 'react-icons/fi';
import { useFeatures } from '../hooks/useFeatures';
import api from '../api';

// 위젯 설정 매핑
const WIDGET_CONFIG = {
  totalStudents: {
    icon: FiUsers,
    label: '전체 학생',
    getValue: (stats) => stats.totalStudents || 0,
    color: '#3B82F6'
  },
  activeTeachers: {
    icon: FiUserCheck,
    label: '활동 강사',
    getValue: (stats) => stats.activeTeachers || 0,
    color: '#8B5CF6'
  },
  activeClasses: {
    icon: FiBook,
    label: '운영 중인 반',
    getValue: (stats) => stats.activeClasses || 0,
    color: '#8B5CF6'
  },
  todayAttendance: {
    icon: FiCalendar,
    label: '오늘 출석',
    getValue: (stats) => stats.todayAttendance || 0,
    color: '#10B981'
  },
  monthlyRevenue: {
    icon: FiDollarSign,
    label: '이달 수납액',
    getValue: (stats) => `${(stats.monthlyRevenue || 0).toLocaleString()}원`,
    color: '#10B981'
  },
  teacherPerformance: {
    icon: FiTrendingUp,
    label: '강사 평균 수업',
    getValue: (stats) => `${(stats.teacherPerformance || 0).toFixed(1)}건`,
    color: '#F59E0B'
  },
  unpaidTuitions: {
    icon: FiDollarSign,
    label: '미수금',
    getValue: (stats) => `${(stats.unpaidTuition || 0).toLocaleString()}원`,
    color: '#F59E0B'
  }
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { widgets } = useFeatures();
  const [stats, setStats] = useState({
    totalStudents: 0,
    todayAttendance: 0,
    activeClasses: 0,
    unpaidTuition: 0,
    activeTeachers: 0,
    monthlyRevenue: 0,
    teacherPerformance: 0
  });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, activityRes] = await Promise.all([
        api.get('/api/dashboard/stats'),
        api.get('/api/dashboard/recent-activity', { params: { limit: 5 } })
      ]);
      setStats(statsRes.data);
      setActivities(activityRes.data.activities || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // operationType에 따른 동적 위젯 생성
  const statCards = widgets
    .filter(widgetName => WIDGET_CONFIG[widgetName]) // 설정된 위젯만 필터링
    .slice(0, 6) // 상위 6개만 표시 (대시보드 상단)
    .map(widgetName => {
      const config = WIDGET_CONFIG[widgetName];
      return {
        icon: config.icon,
        label: config.label,
        value: config.getValue(stats),
        color: config.color
      };
    });

  return (
    <div className="dashboard">
      <div className="page-header">
        <h2>대시보드</h2>
        <p>오늘의 학원 현황을 확인하세요</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <div key={index} className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: `${stat.color}20`, color: stat.color }}>
              <stat.icon />
            </div>
            <div className="stat-info">
              <span className="stat-value">{loading ? '-' : stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="section">
        <h3>빠른 작업</h3>
        <div className="quick-actions">
          <button className="action-btn" onClick={() => navigate('/attendance')}>
            <FiCalendar />
            출석 체크
          </button>
          <button className="action-btn" onClick={() => navigate('/students')}>
            <FiUsers />
            학생 등록
          </button>
          <button className="action-btn" onClick={() => navigate('/lessons')}>
            <FiBook />
            수업 일지 작성
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="section">
        <h3>최근 활동</h3>
        <div className="activity-list">
          {activities.length === 0 ? (
            <p className="empty-message">아직 활동 내역이 없습니다.</p>
          ) : (
            activities.map((activity, index) => (
              <div key={index} className="activity-item">
                <span className={`activity-badge ${activity.type}`}>
                  {activity.type === 'lesson' ? '수업' : '출석'}
                </span>
                <span className="activity-message">{activity.message}</span>
                <span className="activity-time">
                  {format(new Date(activity.time), 'MM/dd HH:mm')}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
